import type { AfterViewInit, ComponentRef, OnDestroy, TemplateRef } from '@angular/core'
import {
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core'
import { NgTemplateOutlet } from '@angular/common'
import { CdkMonitorFocus } from '@angular/cdk/a11y'
import { RowSwipeDirective } from '../../directives/row-swipe/row-swipe.directive'
import { HapticService } from '../../services/haptic-service/haptic.service'
import { IconButtonComponent } from '../icon-button/icon-button.component'
import type { IconButtonSelectedTone } from '../icon-button/icon-button.component'
import { DynamicRefService } from '../../services/dynamic-ref-service/dynamic-ref.service'
import { RowSwipeService } from '../../directives/row-swipe/row-swipe.service'
import { RowActionsSheetComponent } from './row-actions-sheet.component'
import { ToastService } from '../../services/toast-service/toast.service'
import { destroyModal } from '../../functions/destroy'

/** One mobile row action. The feature owns its permissions and handler. */
export interface SwipeRowAction {
  id: string
  label: string
  icon: string
  /** Destructive actions use a red icon in the tray and a danger row in the long-press sheet. */
  variant?: 'destructive'
  selected?: boolean
  selectedTone?: IconButtonSelectedTone
  disabled?: boolean
  iconTemplate?: TemplateRef<unknown>
}

/** Shared mobile row actions, reached by swipe, long press, or keyboard focus. */
@Component({
  selector: 'aw-swipe-row',
  templateUrl: './swipe-row.component.html',
  styleUrl: './swipe-row.component.scss',
  host: { '[attr.data-dividers]': 'dividers()' },
  imports: [RowSwipeDirective, IconButtonComponent, NgTemplateOutlet, CdkMonitorFocus]
})
export class SwipeRowComponent implements AfterViewInit, OnDestroy {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly haptics = inject(HapticService)
  private readonly dynamicRef = inject(DynamicRefService)
  private readonly rowSwipeService = inject(RowSwipeService)
  private readonly toast = inject(ToastService)
  private sheetRef?: ComponentRef<RowActionsSheetComponent>
  readonly disabled = input(false)
  readonly linkToCopy = input<string>()
  readonly label = input.required<string>()
  readonly actionLabel = input(q.translations.common.delete)
  /** Omit to retain the single delete action used by attachments and checklists. */
  readonly actions = input<readonly SwipeRowAction[]>()
  /** Closes an open tray when a virtual row is reused for another entity. */
  readonly rowKey = input<string>()
  readonly dividers = input(true)
  readonly action = output<void>()
  readonly actionSelected = output<string>()
  readonly opened = output<boolean>()
  private readonly swipe = viewChild.required(RowSwipeDirective)
  private readonly rowWidth = signal(0)
  private observer?: ResizeObserver
  readonly rowActions = computed<readonly SwipeRowAction[]>(() => this.getRowActions())
  readonly isDisabled = computed(() => this.disabled() || !this.rowActions().some(action => !action.disabled))
  readonly actionWidth = computed(() => this.getActionWidth())

  constructor() {
    effect(() => {
      this.rowKey()
      untracked(() => this.close())
    })
    effect(() => {
      const disabled = this.isDisabled()
      const label = this.label()
      const actions = this.rowActions()

      untracked(() => {
        if (disabled) {
          this.close()
        } else {
          this.sheetRef?.setInput('label', label)
          this.sheetRef?.setInput('actions', actions)
        }
      })
    })
  }

  /**
   * Adds link copying to the entity actions when a link is available.
   * @withSignal actions, actionLabel, linkToCopy
   * @returns {readonly SwipeRowAction[]} Actions for the tray and long-press sheet.
   */
  private getRowActions(): readonly SwipeRowAction[] {
    const actions = this.actions() ?? [
      { id: 'delete', label: this.actionLabel(), icon: 'delete', variant: 'destructive' as const }
    ]

    if (!this.linkToCopy()) {
      return actions
    }

    return [{ id: 'copyLink', label: q.translations.SwipeRowComponent.copyLink, icon: 'link' }, ...actions]
  }

  /**
   * Fits the action tray inside the row and leaves space for its content.
   * @withSignal {Signal} rowActions, rowWidth
   * @returns {number} Tray width in pixels.
   */
  private getActionWidth(): number {
    // Each compact button is 36px with an 8px gap, plus 8px tray padding.
    const width = this.rowActions().length * 44 + 8
    return this.rowWidth() ? Math.min(width, Math.max(60, this.rowWidth() - 44)) : width
  }

  /** Measures the available row width and keeps long trays inside the viewport. */
  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(entries => this.rowWidth.set(entries[0].contentRect.width))
      this.observer.observe(this.element.nativeElement)
    }
  }

  /** Releases the size observer. */
  ngOnDestroy(): void {
    this.observer?.disconnect()
    this.closeSheet()
  }

  /** Shows the same actions for keyboard and touch users. */
  reveal(): void {
    if (!this.isDisabled()) {
      this.swipe().openRow(false)
    }
  }

  /** Returns the row to its resting position. */
  close(): void {
    this.swipe().closeRow()
    this.closeSheet()
  }

  /** Opens the same actions above the list, outside its clipped or virtual rows. */
  showActionSheet(): void {
    if (this.isDisabled() || this.sheetRef) {
      return
    }

    this.rowSwipeService.closeRows(this.swipe())
    this.swipe().closeRow()
    this.haptics.buttonClick()

    const [ref, sheet] = this.dynamicRef.create(RowActionsSheetComponent)
    const rowKey = this.rowKey()

    this.sheetRef = ref
    ref.setInput('label', this.label())
    ref.setInput('actions', this.rowActions())
    sheet.selected.subscribe(id => {
      if (id === 'copyLink' && this.rowKey() === rowKey) {
        this.haptics.buttonClick()
        void this.copyLink()
      }
    })
    sheet.closed.subscribe(id => {
      this.closeSheet()

      const action = this.rowActions().find(item => item.id === id)

      if (action && id !== 'copyLink' && this.rowKey() === rowKey) {
        this.selectAction(action)
      }
    })
  }

  /** Removes the row's overlay when it closes, becomes disabled, or is reused. */
  private closeSheet(): void {
    if (this.sheetRef) {
      const ref = this.sheetRef

      this.sheetRef = undefined
      destroyModal(ref)
    }
  }

  /**
   * Runs an enabled action once and dismisses the tray before opening any picker.
   * @param {SwipeRowAction} action Action selected by the user.
   */
  selectAction(action: SwipeRowAction): void {
    if (this.isDisabled() || action.disabled) {
      return
    }

    this.close()
    this.haptics.buttonClick()

    if (action.id === 'copyLink') {
      void this.copyLink()
    } else if (this.actions() === undefined) {
      this.action.emit()
    } else {
      this.actionSelected.emit(action.id)
    }
  }

  /** Copies the current entity link and confirms only a successful clipboard write. */
  private async copyLink(): Promise<void> {
    const link = this.linkToCopy()

    if (!link) {
      return
    }

    try {
      await navigator.clipboard.writeText(link)
      this.toast.show(q.translations.SwipeRowComponent.linkCopied, 'confirm', 'circle-check', { hideDelay: 2000 })
    } catch {
      this.toast.show(q.translations.SwipeRowComponent.copyFailed, 'warning')
    }
  }
}
